import { useState } from 'react';
import { Badge, Button, useToast } from '@vd/ui';
import { ArrowRight, Plus } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { ApiToken } from '@vd/types';
import { api } from '../../../lib/api';
import { CreateMcpTokenModal } from '../../../features/modals/CreateMcpTokenModal';
import { DestructiveConfirm } from '../../../features/modals/DestructiveConfirm';
import styles from '../Settings.module.css';
import { formatWhen } from '../../../lib/time';
import { When } from '../../../lib/When';

export function McpTokensTab() {
  const [createOpen, setCreateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<{ id: string; label: string } | null>(null);
  const toast = useToast();
  const qc = useQueryClient();
  const { data: tokens = [] } = useQuery({ queryKey: ['tokens'], queryFn: () => api.listTokens() });
  // A token from a server before `kind` existed is an MCP token, as every one was then.
  const mcpTokens = tokens.filter((t) => (t.kind ?? 'mcp') === 'mcp');
  const otherTokens = tokens.filter((t) => (t.kind ?? 'mcp') !== 'mcp');

  const revoke = async () => {
    if (!revokeTarget) return;
    try {
      await api.revokeToken(revokeTarget.id);
      await qc.invalidateQueries({ queryKey: ['tokens'] });
      toast.push({ variant: 'success', title: 'Token revoked', description: revokeTarget.label });
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Revoke failed', description: (e as Error).message });
    }
  };

  return (
    <>
      <div className={styles.rowSpread}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 className={styles.h1}>MCP tokens</h1>
          <p className={styles.sub}>
            Personal access tokens for any MCP-compatible client to read
            and write your tickets, comments, and captures. Each token
            is bound to one user account, with optional scope limits
            (read-only, scoped to specific projects, etc.).
          </p>
          <div style={{
            display: 'flex', flexDirection: 'column', gap: 6,
            padding: '10px 12px', marginTop: -8, marginBottom: 16,
            border: '1px solid var(--border)', borderRadius: 6,
            background: 'var(--bg-subtle)', fontSize: 13, color: 'var(--text-secondary)',
          }}>
            <div>
              <strong style={{ color: 'var(--text)' }}>Works with:</strong>{' '}
              Claude Code (CLI), Claude Desktop, Grok, Cursor, Cline, Continue,
              Zed AI, and any client that speaks the Model Context Protocol.
              The hosted MCP server lives at{' '}
              <code style={{
                fontSize: 12,
                padding: '1px 4px',
                background: 'var(--bg-muted)',
                borderRadius: 3,
                fontFamily: 'ui-monospace, monospace',
              }}>
                https://mcp.toado.dev/mcp
              </code>.
            </div>
            <div>
              <strong style={{ color: 'var(--text)' }}>Two ways to connect:</strong>{' '}
              <em>OAuth</em> (preferred, zero copy-paste; client opens a browser
              consent flow) or <em>this token-paste flow</em> (older clients
              or scripted setups). Tokens here are for the second path.
            </div>
            <div>
              For setup walkthroughs (config snippets, OAuth flow, troubleshooting),
              see the{' '}
              <Link
                to="/company/settings/mcp-setup"
                style={{
                  color: 'var(--accent)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  textDecoration: 'underline',
                }}
              >
                MCP setup guide <ArrowRight size={12} />
              </Link>.
            </div>
          </div>
        </div>
        <Button variant="primary" leftIcon={<Plus size={14} />} onClick={() => setCreateOpen(true)} style={{ flexShrink: 0 }}>
          Create token
        </Button>
      </div>

      {/* MCP tokens on their own (TOADO-770): the extension and the widget
          make tokens of their own, one per Connect, which buried the ones a
          person created. They are listed below, still revocable. */}
      <section className={`${styles.section} ${styles.tableScroll}`}>
        {mcpTokens.length === 0 && (
          <p className={styles.sub} style={{ margin: 0 }}>No MCP tokens yet. Create one for a client that cannot sign in with OAuth.</p>
        )}
        {mcpTokens.length > 0 && <TokenTable tokens={mcpTokens} onRevoke={setRevokeTarget} />}
      </section>

      {otherTokens.length > 0 && (
        <section className={styles.section}>
          <details>
            <summary style={{ cursor: 'pointer', fontWeight: 500 }}>
              Other connections ({otherTokens.length})
            </summary>
            <p className={styles.sub} style={{ margin: '8px 0' }}>
              Made for you when you connect the browser extension or sign in to a widget&rsquo;s team mode. Revoke
              one to sign that browser out.
            </p>
            <div className={styles.tableScroll}>
              <TokenTable tokens={otherTokens} onRevoke={setRevokeTarget} />
            </div>
          </details>
        </section>
      )}

      <CreateMcpTokenModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <DestructiveConfirm
        open={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onConfirm={revoke}
        title={`Revoke ${revokeTarget?.label ?? 'token'}?`}
        description="Any MCP client (Claude Code, Claude Desktop, Cursor, etc.) using this token will stop working immediately. Past comments keep the token's attribution (marked as revoked)."
        confirmLabel="Revoke"
      />
    </>
  );
}

/** What a token is called in the list: the extension's and the widget's connections by what made them. */
function kindLabel(kind: ApiToken['kind']): string | null {
  if (kind === 'extension') return 'Browser extension';
  if (kind === 'widget_frame') return 'Widget team mode';
  return null;
}

/** A date and time, so "last used" says which day (it used to show the time alone). */
function when(iso: string): string {
  return formatWhen(iso);
}

function TokenTable({ tokens, onRevoke }: { tokens: ApiToken[]; onRevoke: (target: { id: string; label: string }) => void }) {
  const now = Date.now();
  return (
        <table className={styles.table}>
          <thead>
            <tr><th>Label</th><th>Scope</th><th>Created</th><th>Last used</th><th></th></tr>
          </thead>
          <tbody>
            {tokens.map((t) => (
              <tr key={t.id}>
                <td>
                  {t.label}
                  {kindLabel(t.kind) && t.label !== kindLabel(t.kind) && (
                    <span style={{ color: 'var(--text-tertiary)', fontSize: 12, display: 'block' }}>{kindLabel(t.kind)}</span>
                  )}
                  {t.expiresAt && Date.parse(t.expiresAt) <= now && (
                    <span style={{ color: 'var(--warning)', fontSize: 12, display: 'block' }}>Expired {when(t.expiresAt)}</span>
                  )}
                </td>
                <td>
                  <Badge variant={t.scope.readOnly ? 'outline' : 'solid'}>
                    {t.scope.readOnly ? 'Read only' : 'Read / Write'}
                  </Badge>
                </td>
                <td style={{ color: 'var(--text-secondary)' }}><When at={t.createdAt} dateOnly /></td>
                <td style={{ color: 'var(--text-secondary)' }}>
                  {t.lastUsedAt ? when(t.lastUsedAt) : 'Never'}
                </td>
                <td>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRevoke({ id: t.id, label: t.label })}
                  >
                    Revoke
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
  );
}
